<script setup lang="ts">
import { ref, onMounted, onBeforeUnmount, watch, defineProps, defineEmits } from 'vue';
import mapboxgl from 'mapbox-gl';
import { Config } from '@/config';
import { addGeoJsonOverlay, removeGeoJsonOverlay } from '@/helpers/map-layers';
import { DEFAULT_MAP_STYLE } from '@/helpers/map-style';
import type { MapBounds, MapLayerPreview, MapSize } from './mapLayer.types';

// CONST
const accessToken = Config.mapbox.accessToken;
const center = ref<[number, number] | undefined>();
const containerId = ref(`map-${Math.random().toString(36).slice(2)}`);
let map: mapboxgl.Map | null = null;
const isProgrammaticMove = ref(false); // Flag to distinguish between user and programmatic moves
const activeLayerIds = new Set<number>();
let containerObserver: ResizeObserver | null = null;
// Last size the observer acted on. Also doubles as the "have we measured yet" marker: the very
// first callback fires as soon as we start observing and reports the size the map was built at,
// which is nothing to correct.
let observedSize: { width: number; height: number } | null = null;

// PROPS
const props = defineProps<{
  id?: string;
  selectedStyle?: string;
  bounds?: MapBounds | null;
  initialZoom?: number | null;
  layers?: MapLayerPreview[];
  /**
   * The size of the participant's map. The canvas takes its shape, so the area the admin frames is the
   * area that is published, and grows no larger: the participant's map opens at the zoom saved here and
   * cannot zoom out past it, so a larger canvas would frame more than a participant can ever see.
   */
  participantMapSize: MapSize;
}>();

const emit = defineEmits<{
  (e: 'setBounds', payload: { bounds: MapBounds; zoom: number }): void;
}>();

// LIFECYCLE
onMounted(() => {
  if (props.id) containerId.value = props.id;
  initMap();

  if (!map) return;

  if (props.bounds) {
    const savedBounds: mapboxgl.LngLatBoundsLike = [props.bounds.southWest, props.bounds.northEast];
    isProgrammaticMove.value = true;
    map.fitBounds(savedBounds);
  }

  map.on('moveend', () => {
    if (!map) return;
    if (!isProgrammaticMove.value) {
      const currentBounds = map.getBounds();
      if (!currentBounds) return;
      const boundsData: MapBounds = {
        northEast: [currentBounds.getNorthEast().lng, currentBounds.getNorthEast().lat],
        southWest: [currentBounds.getSouthWest().lng, currentBounds.getSouthWest().lat],
      };
      const currentZoom = map.getZoom();

      emit('setBounds', { bounds: boundsData, zoom: currentZoom });
    }
    isProgrammaticMove.value = false;
  });
  map.addControl(new mapboxgl.NavigationControl());

  map.on('load', () => {
    syncLayers();
  });
  map.on('style.load', () => {
    syncLayers();
  });
  map.on('error', (event: mapboxgl.ErrorEvent) => {
    console.error('Mapbox error:', event?.error ?? event);
  });

  observeContainerSize();
});

onBeforeUnmount(() => {
  containerObserver?.disconnect();
  containerObserver = null;
  map?.remove();
  map = null;
});

/**
 * The canvas is fluid-width (up to `participantMapSize`), and Mapbox does not notice its
 * container changing size on its own — it would keep painting at the width it was built at.
 *
 * `map.resize()` emits `moveend`, which is the event that writes bounds back into the form, so
 * every resize is flagged as programmatic first. Without that, merely opening a tab or dragging
 * the window would dirty the form with bounds the admin never chose.
 */
function observeContainerSize() {
  const container = map?.getContainer();
  if (typeof ResizeObserver === 'undefined' || !container) return;

  containerObserver = new ResizeObserver((entries) => {
    const rect = entries[0]?.contentRect;
    if (!map || !rect) return;

    const width = Math.round(rect.width);
    const height = Math.round(rect.height);
    // Mapbox short-circuits `resize()` when the dimensions are unchanged, and a short-circuited
    // resize emits no `moveend` — the flag would then be left set and would swallow the admin's
    // next real move. Bailing out on an unchanged size keeps the flag and the event in step.
    if (observedSize && observedSize.width === width && observedSize.height === height) return;

    const isFirstMeasurement = observedSize === null;
    observedSize = { width, height };
    if (isFirstMeasurement) return;

    isProgrammaticMove.value = true;
    map.resize();
  });
  containerObserver.observe(container);
}

watch(
  () => props.selectedStyle,
  (newStyle) => {
    if (map && newStyle) {
      map.setStyle(newStyle);
    }
  },
);

watch(
  () => props.layers,
  (newLayers) => {
    if (map && map.isStyleLoaded()) {
      syncLayers(newLayers ?? []);
    }
  },
  { deep: true },
);

function initMap() {
  map = new mapboxgl.Map({
    accessToken,
    container: containerId.value,
    style: props.selectedStyle ?? DEFAULT_MAP_STYLE,
    center: center.value ?? [-75.711756, 45.427477],
    zoom: props.initialZoom ?? 9,
    attributionControl: false,
    // Mapbox resizes itself on window `resize`, unflagged, so its `moveend` would write bounds into
    // the form. The container observer already covers that case, and flags every resize it makes.
    trackResize: false,
  });
}

function syncLayers(layers: MapLayerPreview[] = props.layers ?? []) {
  if (!map) return;

  const desiredIds = new Set<number>();
  for (const layer of layers) {
    if (typeof layer?.id === 'number') {
      desiredIds.add(layer.id);
    }
  }

  for (const id of Array.from(activeLayerIds)) {
    if (!desiredIds.has(id)) {
      removeGeoJsonOverlay(map, id);
      activeLayerIds.delete(id);
    }
  }

  for (const layer of layers) {
    if (!layer || typeof layer.id !== 'number') continue;
    activeLayerIds.add(layer.id);
    void addGeoJsonOverlay(map, layer);
  }
}
</script>

<template>
  <div
    class="map-wrapper"
    :style="{
      '--participant-map-width': `${props.participantMapSize.width}px`,
      '--participant-map-aspect-ratio': `${props.participantMapSize.width} / ${props.participantMapSize.height}`,
    }"
  >
    <div :id="containerId" class="map-canvas"></div>
  </div>
</template>

<style lang="scss">
.map-wrapper {
  $padding: 10px;

  border-radius: 8px;
  padding: $padding;
  background-color: $color-neutral-grey-300;
  position: relative;
  width: 100%;
  max-width: calc(var(--participant-map-width) + #{2 * $padding});

  .map-canvas {
    aspect-ratio: var(--participant-map-aspect-ratio);
    width: 100%;
  }

  .saveBoundsBtn {
    position: absolute;
    top: 20px;
    left: 20px;
  }

  .hide {
    opacity: 0;
  }
}
</style>
